import React from "react";
import PropTypes from "prop-types";
import {
  Table,
  Button,
  DatePicker,
  Row,
  Col,
  Card,
  Icon,
  Input,
  Select
} from "antd";
import moment from "moment";

const { RangePicker } = DatePicker;
const { Option } = Select;

const ListAuditTrailView = ({
  auditTrails,
  currentPage,
  totalRecords,
  startDate,
  endDate,
  searchText,
  selectedEvents,
  selectedStatus,
  selectedUserType,
  eventOptions,
  statusOptions,
  userTypeOptions,
  onDateRangeChange,
  onPageChange,
  onRefresh,
  onSearchChange,
  onFilterChange,
  onQuickDateRange,
  onClearFilters
}) => {
  const columns = [
    {
      title: "Event",
      dataIndex: "key",
      key: "key",
      width: 180,
      render: key => (
        <div
          style={{ display: "flex", alignItems: "center", color: "#1890ff" }}
        >
          <Icon type="eye" style={{ marginRight: 8, fontSize: "16px" }} />
          <span>{key || "View Patient"}</span>
        </div>
      )
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      width: 200,
      render: user =>
        user ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Icon type="user" style={{ marginRight: 8, color: "#666" }} />
            <div>
              <div style={{ fontWeight: 500 }}>{user.name}</div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {user.type || "owner"}
              </div>
            </div>
          </div>
        ) : (
          "-"
        )
    },
    {
      title: "Patient/Resource",
      dataIndex: "patient",
      key: "patient",
      width: 200,
      render: (patient, record) => {
        if (patient) {
          return (
            <div>
              <div style={{ fontWeight: 500, color: "#333" }}>
                {patient.name}
              </div>
              <div style={{ fontSize: "12px", color: "#1890ff" }}>
                {record.resource?.type || "ClinicPatient"}
              </div>
            </div>
          );
        }
        if (record.resource) {
          return (
            <div>
              <div style={{ fontWeight: 500, color: "#333" }}>
                Resource {record.resource.id}
              </div>
              <div style={{ fontSize: "12px", color: "#1890ff" }}>
                {record.resource.type}
              </div>
            </div>
          );
        }
        return "-";
      }
    },
    {
      title: "Status",
      dataIndex: "event_status",
      key: "event_status",
      width: 120,
      render: status => {
        const isSuccess = status === "SUCCESS";
        let displayStatus;
        if (isSuccess) {
          displayStatus = "Success";
        } else if (status === "FAILED") {
          displayStatus = "Failure";
        } else {
          displayStatus = status || "Success";
        }

        return (
          <span
            style={{
              color: isSuccess ? "#52c41a" : "#ff4d4f",
              fontWeight: 500,
              padding: "4px 12px",
              backgroundColor: isSuccess ? "#f6ffed" : "#fff2f0",
              borderRadius: "4px",
              border: `1px solid ${isSuccess ? "#b7eb8f" : "#ffccc7"}`,
              fontSize: "12px"
            }}
          >
            {displayStatus}
          </span>
        );
      }
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
      render: timestamp => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Icon type="clock-circle" style={{ marginRight: 8, color: "#666" }} />
          <span>{moment(timestamp).format("MMM DD, YYYY h:mm A")}</span>
        </div>
      ),
      sorter: (a, b) => moment(a.timestamp).unix() - moment(b.timestamp).unix(),
      defaultSortOrder: "descend"
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
      width: 140,
      render: ip => (
        <span
          style={{ fontFamily: "monospace", fontSize: "12px", color: "#666" }}
        >
          {ip}
        </span>
      )
    }
  ];

  const handleDateRangeChange = dates => {
    if (dates && dates.length === 2) {
      const start = dates[0].format("YYYY-MM-DD");
      const end = dates[1].format("YYYY-MM-DD");
      onDateRangeChange(start, end);
    }
  };

  return (
    <div className="audit-trail-list">
      <Card>
        {/* Filters & Search Header */}
        <div className="mb-4">
          <h3 style={{ marginBottom: 4 }}>Filters & Search</h3>
          <p style={{ color: "#666", marginBottom: 20 }}>
            Filter audit logs by user, action, status, or date range
          </p>

          {/* First Row - Search and Dropdowns */}
          <Row gutter={[16, 8]} className="mb-3" align="middle">
            <Col span={5}>
              <Input
                placeholder="Search users, patients, actions..."
                value={searchText}
                onChange={e => onSearchChange(e.target.value)}
                onPressEnter={e => onSearchChange(e.target.value)}
                prefix={<Icon type="search" style={{ color: "#ccc" }} />}
                allowClear
                style={{ height: "32px" }}
              />
            </Col>
            <Col span={1} style={{ textAlign: "center" }}>
              <Icon type="filter" style={{ fontSize: "16px", color: "#666" }} />
            </Col>
            <Col span={6}>
              <Select
                placeholder="All Events"
                value={selectedEvents.length > 0 ? selectedEvents : undefined}
                onChange={values =>
                  onFilterChange("selectedEvents", values || [])
                }
                mode="multiple"
                style={{ width: "100%" }}
                allowClear
                maxTagCount={0}
                maxTagPlaceholder={() => `${selectedEvents.length} selected`}
              >
                {eventOptions.map(event => (
                  <Option key={event} value={event}>
                    {event}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="All Statuses"
                value={selectedStatus.length > 0 ? selectedStatus : undefined}
                onChange={values =>
                  onFilterChange("selectedStatus", values || [])
                }
                mode="multiple"
                style={{ width: "100%" }}
                allowClear
                maxTagCount={0}
                maxTagPlaceholder={() => `${selectedStatus.length} selected`}
              >
                {statusOptions.map(status => (
                  <Option
                    key={status.value || status}
                    value={status.value || status}
                  >
                    {status.label || status}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="All User Types"
                value={
                  selectedUserType.length > 0 ? selectedUserType : undefined
                }
                onChange={values =>
                  onFilterChange("selectedUserType", values || [])
                }
                mode="multiple"
                style={{ width: "100%" }}
                allowClear
                maxTagCount={0}
                maxTagPlaceholder={() => `${selectedUserType.length} selected`}
              >
                {userTypeOptions.map(userType => (
                  <Option
                    key={userType.value || userType}
                    value={userType.value || userType}
                  >
                    {userType.label || userType}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          {/* Second Row - Date Range and Action Buttons */}
          <Row gutter={[8, 8]} align="middle">
            <Col span={6}>
              <RangePicker
                value={[moment(startDate), moment(endDate)]}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Col>
            <Col>
              <Button.Group>
                <Button
                  onClick={() => onQuickDateRange(0)}
                  size="small"
                  type={
                    moment(startDate).isSame(moment(), "day") &&
                    moment(endDate).isSame(moment(), "day")
                      ? "primary"
                      : "default"
                  }
                >
                  Today
                </Button>
                <Button
                  onClick={() => onQuickDateRange(7)}
                  size="small"
                  type={
                    moment(startDate).isSame(
                      moment().subtract(7, "days"),
                      "day"
                    ) && moment(endDate).isSame(moment(), "day")
                      ? "primary"
                      : "default"
                  }
                >
                  7 Days
                </Button>
                <Button
                  onClick={() => onQuickDateRange(30)}
                  size="small"
                  type={
                    moment(startDate).isSame(
                      moment().subtract(30, "days"),
                      "day"
                    ) && moment(endDate).isSame(moment(), "day")
                      ? "primary"
                      : "default"
                  }
                >
                  30 Days
                </Button>
                <Button
                  onClick={() => onQuickDateRange(90)}
                  size="small"
                  type={
                    moment(startDate).isSame(
                      moment().subtract(90, "days"),
                      "day"
                    ) && moment(endDate).isSame(moment(), "day")
                      ? "primary"
                      : "default"
                  }
                >
                  90 Days
                </Button>
              </Button.Group>
              <Button onClick={onClearFilters} size="small">
                Clear All Filters
              </Button>
              <Button icon="reload" onClick={onRefresh} size="small" style={{ marginLeft: 8 }}>
                Refresh
              </Button>
            </Col>
            <Col span={6} className="text-right">
              <span style={{ color: "#666", fontSize: "12px" }}>
                Total: {totalRecords}
              </span>
            </Col>
          </Row>
        </div>

        <Table
          dataSource={auditTrails}
          columns={columns}
          rowKey="id"
          pagination={{
            current: currentPage,
            total: totalRecords,
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `Showing ${range[0]} to ${range[1]} of ${total} entries`,
            onChange: onPageChange,
            style: { marginTop: "16px" }
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>
    </div>
  );
};

ListAuditTrailView.propTypes = {
  auditTrails: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      key: PropTypes.string,
      user: PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string
      }),
      patient: PropTypes.shape({
        name: PropTypes.string
      }),
      resource: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        type: PropTypes.string
      }),
      event_status: PropTypes.string,
      timestamp: PropTypes.string,
      ip_address: PropTypes.string
    })
  ).isRequired,
  currentPage: PropTypes.number.isRequired,
  totalRecords: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  searchText: PropTypes.string.isRequired,
  selectedEvents: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedStatus: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedUserType: PropTypes.arrayOf(PropTypes.string).isRequired,
  eventOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ).isRequired,
  userTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ).isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onQuickDateRange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired
};

export default ListAuditTrailView;
